import Paper from '@/components/Paper'
import { CHART_COLORS } from '@/constants/chartColors'
import { SCHEMES } from '@/components/EchartCustomized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { formatCurrency } from '@/utils/function'
import dayjs from 'dayjs'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { Button, IconButton, Typogaphy } from 'vnetwork-ui'
import MonthRangeSelector, { MonthRange } from '@/components/MonthRangeSelector'
import { getMonthlyServiceBreakdown } from '../../../services'
import { SalesTargetFilters } from '../../../types'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import CustomLegend from './Legend'
import { ArrowUp, ArrowDown, Minus } from 'lucide-react'
import AIInsights from './AIInsights'
import { PDFExportButton, ExcelExportButton } from '../ExportButtons'

interface MonthlyRevenueTrendsProps {
    filters: SalesTargetFilters
}

const MonthlyRevenueTrends = ({ filters }: MonthlyRevenueTrendsProps) => {
    const { t } = useTranslation()
    const chartRef = useRef<HTMLDivElement>(null)
    const [compareRange, setCompareRange] = useState<{
        gte: string | null
        lte: string | null
    }>({ gte: null, lte: null })

    // Calculate comparison period based on selected range
    const comparisonFilters = useMemo(() => {
        if (
            !compareRange.gte ||
            !compareRange.lte ||
            !filters.gte ||
            !filters.lte
        )
            return null

        return {
            gte: compareRange.gte,
            lte: compareRange.lte,
            employee_id: filters?.employee_id ?? [],
        }
    }, [filters, compareRange])

    // Current period data
    const { data, renderState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_BUSINESS_TARGETS,
            'monthly-revenue-trends',
            filters.gte,
            filters.lte,
            filters?.employee_id ?? [],
        ],
        queryFn: () =>
            getMonthlyServiceBreakdown({
                gte: filters.gte,
                lte: filters.lte,
                employee_id: filters?.employee_id ?? [],
            }),
        enabled: !!filters.gte && !!filters.lte,
    })

    // Comparison period data
    const { data: comparisonData } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_BUSINESS_TARGETS,
            'monthly-revenue-trends-comparison',
            comparisonFilters?.gte,
            comparisonFilters?.lte,
            comparisonFilters?.employee_id ?? [],
        ],
        queryFn: () =>
            getMonthlyServiceBreakdown({
                gte: comparisonFilters!.gte,
                lte: comparisonFilters!.lte,
                employee_id: comparisonFilters!.employee_id,
            }),
        enabled: !!comparisonFilters,
    })

    // Process comparison data
    const comparisonChartData = useMemo(() => {
        if (
            !comparisonData ||
            !Array.isArray(comparisonData) ||
            comparisonData.length === 0
        )
            return []

        const monthsSet = new Set<string>()
        comparisonData.forEach((service: any) => {
            service.monthly_breakdown?.forEach((month: any) => {
                monthsSet.add(month.revenue_month)
            })
        })

        const months = Array.from(monthsSet).sort()

        return months.map((month) => {
            let totalRevenue = 0
            let renewal = 0
            let newSale = 0
            let upsell = 0

            comparisonData.forEach((service: any) => {
                const monthData = service.monthly_breakdown?.find(
                    (m: any) => m.revenue_month === month
                )
                if (monthData) {
                    totalRevenue += monthData.total_revenue || 0
                    renewal += monthData.renewal_revenue || 0
                    newSale += monthData.new_sale_revenue || 0
                    upsell += monthData.upsell_revenue || 0
                }
            })

            return {
                month: dayjs(month).format('MM/YYYY'),
                monthIndex: dayjs(month).month(),
                'Tổng doanh thu': totalRevenue,
                'Duy trì': renewal,
                'Bán mới': newSale,
                Upsell: upsell,
            }
        })
    }, [comparisonData])

    const chartData = useMemo(() => {
        if (!data || !Array.isArray(data) || data.length === 0) return []

        const monthsSet = new Set<string>()
        data.forEach((service: any) => {
            service.monthly_breakdown?.forEach((month: any) => {
                monthsSet.add(month.revenue_month)
            })
        })

        const months = Array.from(monthsSet).sort()

        return months.map((month, index) => {
            let totalRevenue = 0
            let renewal = 0
            let newSale = 0
            let upsell = 0

            data.forEach((service: any) => {
                const monthData = service.monthly_breakdown?.find(
                    (m: any) => m.revenue_month === month
                )
                if (monthData) {
                    totalRevenue += monthData.total_revenue || 0
                    renewal += monthData.renewal_revenue || 0
                    newSale += monthData.new_sale_revenue || 0
                    upsell += monthData.upsell_revenue || 0
                }
            })

            // Add comparison data if available
            const compData = comparisonChartData[index]

            return {
                month: dayjs(month).format('MM/YYYY'),
                'Tổng doanh thu': totalRevenue,
                'Duy trì': renewal,
                'Bán mới': newSale,
                Upsell: upsell,
                ...(compData && {
                    'Tổng DT (so sánh)': compData['Tổng doanh thu'],
                    'Duy trì (so sánh)': compData['Duy trì'],
                    'Bán mới (so sánh)': compData['Bán mới'],
                    'Upsell (so sánh)': compData.Upsell,
                }),
            }
        })
    }, [data, comparisonChartData])

    const formatYAxis = (value: number) => {
        const allValues = chartData.flatMap((d) => [
            d['Tổng doanh thu'],
            d['Duy trì'],
            d['Bán mới'],
            d.Upsell,
            d['Tổng DT (so sánh)'] || 0,
            d['Duy trì (so sánh)'] || 0,
            d['Bán mới (so sánh)'] || 0,
            d['Upsell (so sánh)'] || 0,
        ])
        const maxValue = Math.max(...allValues)

        if (maxValue >= 1_000_000_000) {
            return `${(value / 1_000_000_000).toFixed(1)}B`
        } else if (maxValue >= 1_000_000) {
            return `${(value / 1_000_000).toFixed(1)}M`
        } else {
            return `${(value / 1_000).toFixed(0)}K`
        }
    }

    const hasComparison = !!compareRange.gte && !!compareRange.lte

    // Format period labels for legend
    const currentPeriodLabel = useMemo(() => {
        if (!filters.gte || !filters.lte) return ''
        return `${dayjs(filters.gte).format('MM/YYYY')} - ${dayjs(filters.lte).format('MM/YYYY')}`
    }, [filters.gte, filters.lte])

    const comparePeriodLabel = useMemo(() => {
        if (!compareRange.gte || !compareRange.lte) return ''
        return `${dayjs(compareRange.gte).format('MM/YYYY')} - ${dayjs(compareRange.lte).format('MM/YYYY')}`
    }, [compareRange.gte, compareRange.lte])

    // Helper to render change indicator
    const renderChangeIndicator = (current: number, compare: number) => {
        if (!compare) return null
        const diff = current - compare
        const percent =
            compare !== 0 ? ((diff / compare) * 100).toFixed(1) : '0'

        if (diff > 0) {
            return (
                <span className="inline-flex items-center justify-center text-green-600 text-xs mr-2 min-w-[50px]">
                    <ArrowUp className="w-3 h-3" />
                    {percent}%
                </span>
            )
        } else if (diff < 0) {
            return (
                <span className="inline-flex items-center justify-center text-red-600 text-xs mr-2 min-w-[50px]">
                    <ArrowDown className="w-3 h-3" />
                    {Math.abs(Number(percent))}%
                </span>
            )
        }
        return (
            <span className="inline-flex items-center justify-center text-gray-400 text-xs mr-2 min-w-[50px]">
                <Minus className="w-3 h-3" />
            </span>
        )
    }

    // Format currency with gray color for 0
    const formatCurrencyWithZero = (value: number) => {
        if (value === 0) {
            return <span className="text-gray-400">-</span>
        }
        return formatCurrency(value)
    }

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-3 border border-gray-300 rounded shadow-lg max-w-xs">
                    <p className="font-medium mb-2">{label}</p>
                    {payload.map((entry: any, index: number) => (
                        <p
                            key={index}
                            className="text-sm"
                            style={{ color: entry.color }}
                        >
                            {entry.name}: {formatCurrency(entry.value)}
                        </p>
                    ))}
                </div>
            )
        }
        return null
    }

    // Prepare export data for table
    const exportData = useMemo(() => {
        return chartData.map((row) => ({
            [t('common.month')]: row.month,
            [t('employees.performance.total_revenue')]: row['Tổng doanh thu'],
            [t('employees.performance.renewal')]: row['Duy trì'],
            [t('employees.performance.new_sale')]: row['Bán mới'],
            [t('employees.performance.upsell')]: row.Upsell,
            ...(hasComparison
                ? {
                      [`${t('employees.performance.total_revenue')} (SS)`]:
                          row['Tổng DT (so sánh)'] || 0,
                      [`${t('employees.performance.renewal')} (SS)`]:
                          row['Duy trì (so sánh)'] || 0,
                      [`${t('employees.performance.new_sale')} (SS)`]:
                          row['Bán mới (so sánh)'] || 0,
                      [`${t('employees.performance.upsell')} (SS)`]:
                          row['Upsell (so sánh)'] || 0,
                  }
                : {}),
        }))
    }, [chartData, hasComparison, t])

    const renderComponent = renderState()
    if (renderComponent) return renderComponent
    return (
        <Paper className="space-y-3">
            <div className="flex items-center justify-between">
                <Typogaphy size="medium" className="font-medium">
                    {t('employees.performance.monthly_revenue_trends')}
                </Typogaphy>

                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">So sánh với:</span>
                    {!hasComparison ? (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                                // Default to previous year same period
                                const startDate = dayjs(filters.gte)
                                const endDate = dayjs(filters.lte)
                                setCompareRange({
                                    gte: startDate
                                        .subtract(1, 'year')
                                        .format('YYYY-MM-DD'),
                                    lte: endDate
                                        .subtract(1, 'year')
                                        .format('YYYY-MM-DD'),
                                })
                            }}
                            className="!h-[38px] !min-w-[200px]"
                        >
                            {t('employees.performance.select_range')}
                        </Button>
                    ) : (
                        <>
                            <IconButton
                                size="small"
                                className="border"
                                onClick={() => {
                                    setCompareRange({ gte: null, lte: null })
                                }}
                            >
                                <DeleteOutlineIcons />
                            </IconButton>
                            <MonthRangeSelector
                                className="min-w-[200px]"
                                placement="right"
                                value={{
                                    gte: compareRange.gte!,
                                    lte: compareRange.lte!,
                                }}
                                onChange={(range: MonthRange) => {
                                    setCompareRange({
                                        gte: range.gte,
                                        lte: range.lte,
                                    })
                                }}
                            />
                        </>
                    )}
                </div>
            </div>

            <CustomLegend
                hasComparison={hasComparison}
                currentPeriodLabel={currentPeriodLabel}
                comparePeriodLabel={comparePeriodLabel}
            />

            <div ref={chartRef} className="relative">
                <div className="absolute top-0 right-0">
                    <PDFExportButton
                        chartRef={chartRef}
                        filename="monthly-revenue-trends"
                    />
                </div>
                <ResponsiveContainer width="100%" height={350}>
                    <LineChart
                        data={chartData}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={SCHEMES.gray}
                        />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis
                            tickFormatter={formatYAxis}
                            tick={{ fontSize: 12 }}
                            width={60}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        {/* Current period lines */}
                        <Line
                            type="monotone"
                            dataKey="Tổng doanh thu"
                            stroke={CHART_COLORS.PRIMARY_BLUE}
                            strokeWidth={3}
                            dot={{ fill: CHART_COLORS.PRIMARY_BLUE, r: 4 }}
                            activeDot={{ r: 6 }}
                            legendType="none"
                        />
                        <Line
                            type="monotone"
                            dataKey="Duy trì"
                            stroke={CHART_COLORS.EMERALD_GREEN}
                            strokeWidth={2}
                            dot={{ fill: CHART_COLORS.EMERALD_GREEN, r: 3 }}
                            legendType="none"
                        />
                        <Line
                            type="monotone"
                            dataKey="Bán mới"
                            stroke={CHART_COLORS.INDIGO_PURPLE}
                            strokeWidth={2}
                            dot={{ fill: CHART_COLORS.INDIGO_PURPLE, r: 3 }}
                            legendType="none"
                        />
                        <Line
                            type="monotone"
                            dataKey="Upsell"
                            stroke={CHART_COLORS.AMBER_ORANGE}
                            strokeWidth={2}
                            dot={{ fill: CHART_COLORS.AMBER_ORANGE, r: 3 }}
                            legendType="none"
                        />
                        {/* Comparison period lines (dashed) */}
                        {hasComparison && (
                            <>
                                <Line
                                    type="monotone"
                                    dataKey="Tổng DT (so sánh)"
                                    stroke={CHART_COLORS.PRIMARY_BLUE}
                                    strokeWidth={2}
                                    strokeDasharray="5 5"
                                    dot={{
                                        fill: CHART_COLORS.PRIMARY_BLUE,
                                        r: 3,
                                    }}
                                    legendType="none"
                                />
                                <Line
                                    type="monotone"
                                    dataKey="Duy trì (so sánh)"
                                    stroke={CHART_COLORS.EMERALD_GREEN}
                                    strokeWidth={1}
                                    strokeDasharray="5 5"
                                    dot={{
                                        fill: CHART_COLORS.EMERALD_GREEN,
                                        r: 2,
                                    }}
                                    legendType="none"
                                />
                                <Line
                                    type="monotone"
                                    dataKey="Bán mới (so sánh)"
                                    stroke={CHART_COLORS.INDIGO_PURPLE}
                                    strokeWidth={1}
                                    strokeDasharray="5 5"
                                    dot={{
                                        fill: CHART_COLORS.INDIGO_PURPLE,
                                        r: 2,
                                    }}
                                    legendType="none"
                                />
                                <Line
                                    type="monotone"
                                    dataKey="Upsell (so sánh)"
                                    stroke={CHART_COLORS.AMBER_ORANGE}
                                    strokeWidth={1}
                                    strokeDasharray="5 5"
                                    dot={{
                                        fill: CHART_COLORS.AMBER_ORANGE,
                                        r: 2,
                                    }}
                                    legendType="none"
                                />
                            </>
                        )}
                    </LineChart>
                </ResponsiveContainer>
            </div>
            <AIInsights
                chartData={chartData}
                comparisonData={comparisonChartData}
                periodRange={currentPeriodLabel}
                comparisonPeriodRange={comparePeriodLabel}
                hasComparison={hasComparison}
            />

            {/* Detail Table */}
            {chartData.length > 0 && (
                <div className="mt-4 overflow-x-auto">
                    <div className="flex justify-end mb-2">
                        <ExcelExportButton
                            data={exportData}
                            filename="monthly-revenue-trends"
                        />
                    </div>
                    <table className="w-full text-sm border-collapse">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="border border-gray-200 px-3 py-2 text-left font-medium">
                                    {t('common.month')}
                                </th>
                                <th className="border border-gray-200 px-3 py-2 text-right font-medium">
                                    {t('employees.performance.total_revenue')}
                                </th>
                                <th className="border border-gray-200 px-3 py-2 text-right font-medium">
                                    {t('employees.performance.renewal')}
                                </th>
                                <th className="border border-gray-200 px-3 py-2 text-right font-medium">
                                    {t('employees.performance.new_sale')}
                                </th>
                                <th className="border border-gray-200 px-3 py-2 text-right font-medium">
                                    {t('employees.performance.upsell')}
                                </th>
                                {hasComparison && (
                                    <>
                                        <th className="border border-gray-200 px-3 py-2 text-right font-medium bg-gray-100">
                                            {t(
                                                'employees.performance.total_revenue'
                                            )}{' '}
                                            (SS)
                                        </th>
                                        <th className="border border-gray-200 px-3 py-2 text-right font-medium bg-gray-100">
                                            {t('employees.performance.renewal')}{' '}
                                            (SS)
                                        </th>
                                        <th className="border border-gray-200 px-3 py-2 text-right font-medium bg-gray-100">
                                            {t(
                                                'employees.performance.new_sale'
                                            )}{' '}
                                            (SS)
                                        </th>
                                        <th className="border border-gray-200 px-3 py-2 text-right font-medium bg-gray-100">
                                            {t('employees.performance.upsell')}{' '}
                                            (SS)
                                        </th>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {chartData.map((row, index) => (
                                <tr key={index} className="hover:bg-gray-50">
                                    <td className="border border-gray-200 px-3 py-2 font-medium">
                                        {row.month}
                                    </td>
                                    <td className="border border-gray-200 px-3 py-2 text-right">
                                        {hasComparison &&
                                            renderChangeIndicator(
                                                row['Tổng doanh thu'],
                                                row['Tổng DT (so sánh)'] || 0
                                            )}
                                        {formatCurrencyWithZero(
                                            row['Tổng doanh thu']
                                        )}
                                    </td>
                                    <td className="border border-gray-200 px-3 py-2 text-right">
                                        {hasComparison &&
                                            renderChangeIndicator(
                                                row['Duy trì'],
                                                row['Duy trì (so sánh)'] || 0
                                            )}
                                        {formatCurrencyWithZero(row['Duy trì'])}
                                    </td>
                                    <td className="border border-gray-200 px-3 py-2 text-right">
                                        {hasComparison &&
                                            renderChangeIndicator(
                                                row['Bán mới'],
                                                row['Bán mới (so sánh)'] || 0
                                            )}
                                        {formatCurrencyWithZero(row['Bán mới'])}
                                    </td>
                                    <td className="border border-gray-200 px-3 py-2 text-right">
                                        {hasComparison &&
                                            renderChangeIndicator(
                                                row.Upsell,
                                                row['Upsell (so sánh)'] || 0
                                            )}
                                        {formatCurrencyWithZero(row.Upsell)}
                                    </td>
                                    {hasComparison && (
                                        <>
                                            <td className="border border-gray-200 px-3 py-2 text-right bg-gray-50">
                                                {formatCurrencyWithZero(
                                                    row['Tổng DT (so sánh)'] ||
                                                        0
                                                )}
                                            </td>
                                            <td className="border border-gray-200 px-3 py-2 text-right bg-gray-50">
                                                {formatCurrencyWithZero(
                                                    row['Duy trì (so sánh)'] ||
                                                        0
                                                )}
                                            </td>
                                            <td className="border border-gray-200 px-3 py-2 text-right bg-gray-50">
                                                {formatCurrencyWithZero(
                                                    row['Bán mới (so sánh)'] ||
                                                        0
                                                )}
                                            </td>
                                            <td className="border border-gray-200 px-3 py-2 text-right bg-gray-50">
                                                {formatCurrencyWithZero(
                                                    row['Upsell (so sánh)'] || 0
                                                )}
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))}
                            {/* Total row */}
                            <tr className="bg-gray-100 font-medium">
                                <td className="border border-gray-200 px-3 py-2">
                                    {t('common.total')}
                                </td>
                                <td className="border border-gray-200 px-3 py-2 text-right">
                                    {hasComparison &&
                                        renderChangeIndicator(
                                            chartData.reduce(
                                                (sum, row) =>
                                                    sum + row['Tổng doanh thu'],
                                                0
                                            ),
                                            chartData.reduce(
                                                (sum, row) =>
                                                    sum +
                                                    (row['Tổng DT (so sánh)'] ||
                                                        0),
                                                0
                                            )
                                        )}
                                    {formatCurrency(
                                        chartData.reduce(
                                            (sum, row) =>
                                                sum + row['Tổng doanh thu'],
                                            0
                                        )
                                    )}
                                </td>
                                <td className="border border-gray-200 px-3 py-2 text-right">
                                    {hasComparison &&
                                        renderChangeIndicator(
                                            chartData.reduce(
                                                (sum, row) =>
                                                    sum + row['Duy trì'],
                                                0
                                            ),
                                            chartData.reduce(
                                                (sum, row) =>
                                                    sum +
                                                    (row['Duy trì (so sánh)'] ||
                                                        0),
                                                0
                                            )
                                        )}
                                    {formatCurrency(
                                        chartData.reduce(
                                            (sum, row) => sum + row['Duy trì'],
                                            0
                                        )
                                    )}
                                </td>
                                <td className="border border-gray-200 px-3 py-2 text-right">
                                    {hasComparison &&
                                        renderChangeIndicator(
                                            chartData.reduce(
                                                (sum, row) =>
                                                    sum + row['Bán mới'],
                                                0
                                            ),
                                            chartData.reduce(
                                                (sum, row) =>
                                                    sum +
                                                    (row['Bán mới (so sánh)'] ||
                                                        0),
                                                0
                                            )
                                        )}
                                    {formatCurrency(
                                        chartData.reduce(
                                            (sum, row) => sum + row['Bán mới'],
                                            0
                                        )
                                    )}
                                </td>
                                <td className="border border-gray-200 px-3 py-2 text-right">
                                    {hasComparison &&
                                        renderChangeIndicator(
                                            chartData.reduce(
                                                (sum, row) => sum + row.Upsell,
                                                0
                                            ),
                                            chartData.reduce(
                                                (sum, row) =>
                                                    sum +
                                                    (row['Upsell (so sánh)'] ||
                                                        0),
                                                0
                                            )
                                        )}
                                    {formatCurrency(
                                        chartData.reduce(
                                            (sum, row) => sum + row.Upsell,
                                            0
                                        )
                                    )}
                                </td>
                                {hasComparison && (
                                    <>
                                        <td className="border border-gray-200 px-3 py-2 text-right bg-gray-100">
                                            {formatCurrency(
                                                chartData.reduce(
                                                    (sum, row) =>
                                                        sum +
                                                        (row[
                                                            'Tổng DT (so sánh)'
                                                        ] || 0),
                                                    0
                                                )
                                            )}
                                        </td>
                                        <td className="border border-gray-200 px-3 py-2 text-right bg-gray-100">
                                            {formatCurrency(
                                                chartData.reduce(
                                                    (sum, row) =>
                                                        sum +
                                                        (row[
                                                            'Duy trì (so sánh)'
                                                        ] || 0),
                                                    0
                                                )
                                            )}
                                        </td>
                                        <td className="border border-gray-200 px-3 py-2 text-right bg-gray-100">
                                            {formatCurrency(
                                                chartData.reduce(
                                                    (sum, row) =>
                                                        sum +
                                                        (row[
                                                            'Bán mới (so sánh)'
                                                        ] || 0),
                                                    0
                                                )
                                            )}
                                        </td>
                                        <td className="border border-gray-200 px-3 py-2 text-right bg-gray-100">
                                            {formatCurrency(
                                                chartData.reduce(
                                                    (sum, row) =>
                                                        sum +
                                                        (row[
                                                            'Upsell (so sánh)'
                                                        ] || 0),
                                                    0
                                                )
                                            )}
                                        </td>
                                    </>
                                )}
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </Paper>
    )
}

export default MonthlyRevenueTrends

// Export data for AIInsights
export { type MonthlyRevenueTrendsProps }
